import {
  Button,
  Chip,
  ChipGroup,
  Icon,
  Input,
  Modal,
  PageHeader,
  Select,
  Table,
  TableColumn,
} from '@components';
import { usePermissionContext } from '@contexts/PermissionProvider';
import { useModal } from '@hooks';
import BudgetIncomeAPICaller, {
  BudgetIncomeFilters,
} from '@services/api/budgetIncome';
import BudgetTemplateAPICaller from '@services/api/budgetTemplate';
import CategoryAPICaller, { CategoryFilters } from '@services/api/category';
import CostCenterAPICaller from '@services/api/costCenter';
import { Budget } from 'interfaces/Budget';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useConfirmationModal } from '@hooks/useConfirmationModal';
import { monthsChipsEnum } from 'enums';
import { BudgetTemplate } from 'interfaces/BudgetTemplate';
import { Category } from 'interfaces/Category';
import { CostCenter } from 'interfaces/CostCenter';
import { toast } from 'react-toastify';
import { groupOptions } from '@components/Input/Select';
import { useFilters } from '@hooks/useFilters';

const ALL_BUDGETS = 'clearOptions';
const ALL_BUDGETS_LABEL = 'TODOS';

export default function BudgetIncomePage() {
  const {
    watch,
    register,
    setValue,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const { isOpen, toggle: toggleBudgetModal } = useModal();
  const [allMonthChips, setAllMonthChips] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const { hasPermission } = usePermissionContext();

  const confirmation = useConfirmationModal();
  const [tableData, setTableData] = useState<{
    total: number;
    result: Array<Budget>;
  }>({
    total: 0,
    result: [],
  });
  const { filters, updateFilters: setFilters, clearFilters } = useFilters();

  const [categories, setCategories] = useState<Array<Category>>([]);
  const [costCenters, setCostCenters] = useState<Array<CostCenter>>([]);
  const [budgetTemplates, setBudgetTemplates] = useState<Array<BudgetTemplate>>(
    []
  );
  const [budgetTemplatesFilter, setBudgetTemplatesFilter] = useState<
    Array<BudgetTemplate>
  >([]);

  const [monthChips, setMonthChips] = useState(monthsChipsEnum);

  const categoriesPageSize = 50;

  const list = (filters: BudgetIncomeFilters) => {
    setIsLoading(true);
    BudgetIncomeAPICaller.list(filters)
      .then((values) => {
        setTableData(values);
      })
      .finally(() => setIsLoading(false));
  };

  const loadCostCenters = useCallback(() => {
    if (hasPermission('costCenters', 'read')) {
      CostCenterAPICaller.listAll().then((values) => {
        setCostCenters(values.result);
      });
    }
  }, [hasPermission]);

  const loadCategories = async (categoryFilters: CategoryFilters) => {
    await CategoryAPICaller.list(categoryFilters).then((values) => {
      setCategories(values.result);
    });
  };

  const loadBudgetTemplates = useCallback(() => {
    if (hasPermission('budgetTemplates', 'read')) {
      BudgetTemplateAPICaller.list().then((values) => {
        setBudgetTemplates(values.result);
      });
    }
  }, [hasPermission]);

  const loadBudgetTemplatesFilter = useCallback(() => {
    if (hasPermission('budgetTemplates', 'read')) {
      BudgetTemplateAPICaller.list().then((values) => {
        values.result.unshift({
          id: ALL_BUDGETS,
          name: ALL_BUDGETS_LABEL,
        });
        setBudgetTemplatesFilter(values.result);
        setIsReady(true);
      });
    }
  }, [hasPermission]);

  useEffect(() => {
    loadCostCenters();
    loadBudgetTemplates();
    loadBudgetTemplatesFilter();
  }, [loadBudgetTemplates, loadBudgetTemplatesFilter, loadCostCenters]);

  useEffect(() => {
    if (!isReady) return;
    list(filters);
  }, [filters, isReady]);

  useEffect(() => clearFilters(), [clearFilters]);

  const handleChipClick = (index: number) => {
    let newMonthChips = monthChips.map((monthChip, monthChipIndex) => {
      if (monthChipIndex !== index) return monthChip;

      return {
        ...monthChip,
        active: !monthChip.active,
      };
    });

    setMonthChips(newMonthChips);
    setValue(
      'months',
      newMonthChips
        .filter((monthChip) => monthChip.active)
        .map((monthChip) => monthChip.key)
    );
  };

  const handleAllMonthChipsClick = () => {
    setAllMonthChips(!allMonthChips);
    setMonthChips(
      monthChips.map((monthChip) => {
        monthChip.active = !monthChip.disabled && !allMonthChips;
        return monthChip;
      })
    );

    setValue(
      'months',
      monthChips
        .filter((monthChip) => monthChip.active)
        .map((monthChip) => monthChip.key)
    );
  };

  const openEditModal = async (editData: any) => {
    const budget = await BudgetIncomeAPICaller.fetch(editData);
    await loadCategories({
      costCenterId: budget.category.costCenter.id,
      size: categoriesPageSize,
    });

    setValue('id', budget.id);
    setValue('name', budget.name);
    setValue('obs', budget.obs);
    setValue('customer', budget.customer);
    setValue('valueCents', budget.valueCents);
    setValue('months', budget.months);
    setValue('costCenterName', budget.category.costCenter.name);
    setValue('costCenterId', budget.category.costCenter.id);
    setValue('categoryName', budget.category.name);
    setValue('categoryId', budget.category.id);
    setValue(
      'budgetTemplateName',
      `${budget.budgetTemplate.name} - ${budget.budgetTemplate.year}`
    );
    setValue('budgetTemplateId', budget.budgetTemplate.id);

    setMonthChips((prevMonthChips) => {
      return prevMonthChips.map((monthChip) => {
        return {
          ...monthChip,
          active: budget.months.includes(monthChip.key),
          disabled: !budget.budgetTemplate.months.includes(monthChip.key),
        };
      });
    });

    toggleBudgetModal();
  };

  const handleDelete = async (id: string) => {
    confirmation({
      title: 'Excluir budget - receita',
      confirmButtonTitle: 'Excluir',
      description: 'Deseja realmente excluir esse budget - receita?',
      onSubmit: () =>
        BudgetIncomeAPICaller.remove(id).then((_) => list(filters)),
    });
  };

  const handleCreateOrUpdate = async (data: Budget) => {
    BudgetIncomeAPICaller.createOrUpdate(data, setError)
      .then((res) => {
        if (res.data.errors) return;
        toggleBudgetModal();
        list(filters);
      })
      .catch((error) => {
        const message =
          error.response.data.message || 'Erro ao cadastrar receita!';
        toast.error(message);
      });
  };

  return (
    <>
      <div className="p-s-300">
        <PageHeader title="Budget - receitas" />

        <div className="mt-s-200">
          <Table
            data={tableData.result}
            totalRecords={tableData.total}
            showColumnSelector={false}
            filters={filters}
            setFilters={setFilters}
            withPagination
            isLoading={isLoading}
            withSearchInput
            additionalFilters={
              <div className="d-flex justify-end">
                <div className="col-sm-7">
                  <Select
                    value={{
                      label: filters.budgetTemplate
                        ? `${filters.budgetTemplateName} - ${filters.budgetTemplate?.year}`
                        : 'Filtrar por modelo de budget',
                      value: filters.budgetTemplateId,
                    }}
                    groupValue={'Budget - ' + filters.budgetTemplate?.year}
                    options={groupOptions(budgetTemplatesFilter, {
                      optionLabel: 'name',
                      optionValue: 'id',
                      groupLabel: 'year',
                      groupPrefix: 'Budget',
                    })}
                    onSelect={(budgetTemplate: any) => {
                      if (budgetTemplate.value !== ALL_BUDGETS) {
                        setFilters({
                          budgetTemplateId: budgetTemplate.value,
                          budgetTemplateName: budgetTemplate.label,
                          budgetTemplate: budgetTemplatesFilter.find(
                            (item) => item.id === budgetTemplate.value
                          ),
                        });
                        return;
                      }

                      const { budgetTemplateId, ...updatedFilters } = filters;
                      setFilters({
                        ...updatedFilters,
                        budgetTemplateId: '',
                        budgetTemplateName: ALL_BUDGETS_LABEL,
                        budgetTemplate: undefined,
                      });
                    }}
                  />
                </div>

                <Button
                  className="ml-s-200 col-sm-5"
                  suffixes={<Icon>add</Icon>}
                  design="filled"
                  onClick={async () => {
                    setValue('id', undefined);
                    setValue('name', undefined);
                    setValue('obs', undefined);
                    setValue('customer', undefined);
                    setValue('valueCents', undefined);
                    setValue('months', undefined);

                    if (costCenters.length === 1) {
                      setValue('costCenterId', costCenters[0].id);
                      setValue('costCenterName', costCenters[0].name);

                      await loadCategories({
                        costCenterId: costCenters[0].id,
                        size: categoriesPageSize,
                      });

                      if (categories.length === 1) {
                        setValue('categoryId', categories[0].id);
                        setValue('categoryName', categories[0].name);
                      } else {
                        setValue('categoryId', undefined);
                        setValue('categoryName', undefined);
                      }
                    } else {
                      setValue('costCenterId', undefined);
                      setValue('costCenterName', undefined);
                      setValue('categoryId', undefined);
                      setValue('categoryName', undefined);
                    }

                    setValue('budgetTemplateId', undefined);
                    setValue('budgetTemplateName', undefined);
                    setMonthChips((prevMonthChips) => {
                      return prevMonthChips.map((monthChip) => {
                        return {
                          ...monthChip,
                          active: false,
                          disabled: false,
                        };
                      });
                    });
                    return toggleBudgetModal();
                  }}
                >
                  Nova receita
                </Button>
              </div>
            }
          >
            <TableColumn
              fromKey="category"
              header="Centro de custo"
              render={(category) => category.costCenter.name}
            />
            <TableColumn
              fromKey="category"
              header="Categoria"
              render={(category) => category.name}
            />
            <TableColumn
              fromKey="budgetTemplate"
              header="Modelo de budget"
              render={(budgetTemplate) =>
                `${budgetTemplate.name} - ${budgetTemplate.year}`
              }
            />
            <TableColumn fromKey="name" header="Descrição" />
            <TableColumn fromKey="customer" header="Cliente" />
            <TableColumn fromKey="valueCents" header="Valor a receber" />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Editar"
              disableSorting
              render={(id) => {
                return (
                  <Button
                    design="transparent"
                    prefixes={<Icon>edit</Icon>}
                    onClick={() => {
                      openEditModal(id);
                    }}
                  />
                );
              }}
            />
            <TableColumn
              width="5%"
              fromKey="id"
              header="Deletar"
              disableSorting
              render={(id) => {
                return (
                  <Button
                    design="transparent"
                    prefixes={<Icon>delete</Icon>}
                    onClick={async () => {
                      handleDelete(id);
                    }}
                  />
                );
              }}
            />
          </Table>
        </div>
        <Modal
          isOpen={isOpen}
          onClickAway={toggleBudgetModal}
          size="medium"
          title="Cadastrar receita"
        >
          <form
            onSubmit={handleSubmit((data) => {
              data.months = monthChips
                .filter((monthChip) => monthChip.active)
                .map((monthChip) => {
                  return monthChip.key;
                });

              handleCreateOrUpdate(data as Budget);
            })}
          >
            <div className="row gap-x-2 mb-s-200">
              <div className="col-sm-6">
                <Select
                  label="Modelo de budget"
                  value={watch('budgetTemplateName')}
                  error={!!errors.budgetTemplateId}
                  caption={errors.budgetTemplateId?.message as string}
                  placeholder="Selecione o modelo de budget"
                  form={register('budgetTemplateId', {
                    required: 'Obrigatório',
                  })}
                  onSelect={(budgetTemplate: any) => {
                    const budgetTemplateMonths = budgetTemplates.find(
                      (bt) => bt.id === budgetTemplate.value
                    )?.months;

                    if (budgetTemplate.value !== watch('budgetTemplateId')) {
                      setValue('months', []);
                      setMonthChips((prevMonthChips) => {
                        return prevMonthChips.map((monthChip) => {
                          return {
                            ...monthChip,
                            active: false,
                            disabled: !budgetTemplateMonths?.includes(
                              monthChip.key
                            ),
                          };
                        });
                      });
                    }

                    setValue('budgetTemplateId', budgetTemplate.value);
                    setValue('budgetTemplateName', budgetTemplate.label);
                  }}
                  options={budgetTemplates.map((budgetTemplate) => {
                    return {
                      label: `${budgetTemplate.name} - ${budgetTemplate.year}`,
                      value: budgetTemplate.id,
                    };
                  })}
                />
              </div>

              <div className="col-sm-6">
                <Select
                  label="Centro de custo"
                  value={watch('costCenterName')}
                  error={!!errors.costCenterId}
                  caption={errors.costCenterId?.message as string}
                  placeholder="Selecione um centro de custo"
                  form={register('costCenterId', {
                    required: 'Obrigatório',
                  })}
                  onSelect={async (costCenter: any) => {
                    setValue('costCenterId', costCenter.value);
                    setValue('costCenterName', costCenter.label);
                    setValue('categoryId', '');
                    setValue('categoryName', 'Selecione uma categoria');
                    await loadCategories({
                      costCenterId: costCenter.value,
                      size: categoriesPageSize,
                    });
                  }}
                  options={costCenters.map((costCenter) => {
                    return {
                      label: costCenter.name,
                      value: costCenter.id,
                    };
                  })}
                />
              </div>
            </div>

            <div className="row gap-x-2 mb-s-200">
              <div className="col-sm-6">
                <Select
                  label="Categoria"
                  value={watch('categoryName')}
                  error={!!errors.categoryId}
                  caption={errors.categoryId?.message as string}
                  placeholder="Selecione uma categoria"
                  form={register('categoryId', { required: 'Obrigatório' })}
                  onSelect={(category: any) => {
                    setValue('categoryId', category.value);
                    setValue('categoryName', category.label);
                  }}
                  options={categories.map((category) => {
                    return {
                      label: category.name,
                      value: category.id,
                    };
                  })}
                  disabled={!watch('costCenterId')}
                />
              </div>

              <div className="col-sm-6">
                <Input
                  label="Descrição"
                  error={!!errors.name}
                  caption={errors.name?.message as string}
                  placeholder="Descrição"
                  form={register('name', { required: 'Obrigatório' })}
                />
              </div>
            </div>
            <div className="row gap-x-2 mb-s-200">
              <div className="col-sm-6">
                <Input
                  label="Cliente"
                  error={!!errors.customer}
                  caption={errors.customer?.message as string}
                  placeholder="Digite um cliente"
                  form={register('customer')}
                />
              </div>
              <div className="col-sm-6">
                <Input
                  label="Valor a receber"
                  error={!!errors.valueCents}
                  caption={errors.valueCents?.message as string}
                  placeholder="R$"
                  mask="currency"
                  form={register('valueCents', { required: 'Obrigatório' })}
                />
              </div>
            </div>
            <div className="row gap-x-2 mb-s-200">
              <div className="col-sm-6">
                <Input
                  label="Observação"
                  error={!!errors.obs}
                  caption={errors.obs?.message as string}
                  placeholder="Observação"
                  form={register('obs')}
                />
              </div>
            </div>
            <label className="form-input__label">Mês</label>
            <div className="row gap-x-2 mb-s-200">
              <div className="col-sm-12">
                <div className="d-flex overflow-x-auto">
                  <Chip
                    key={'all'}
                    size="small"
                    className={`mr-s-100 ${allMonthChips ? 'chip-active' : ''}`}
                    onClick={() => handleAllMonthChipsClick()}
                  >
                    Todos
                  </Chip>

                  <ChipGroup
                    chips={monthChips}
                    onChipClick={handleChipClick}
                    sizeChips="small"
                    error={!!errors.months}
                    caption={errors.months?.message as string}
                    form={register('months', { required: 'Obrigatório' })}
                  />
                </div>
              </div>
            </div>
            <div className="d-flex justify-end">
              <Button className="pl-s-900 pr-s-900" type="submit">
                Salvar
              </Button>
            </div>
          </form>
        </Modal>
      </div>
    </>
  );
}
